// ============================================================
// WHEN AN EFFECT APPLIES - a small condition on an effect.
//
// "+10 Weapon Skill WHEN the target is Hulking or larger", "+2 damage WHEN
// in melee AND the target is Xenos". A `When` is groups of clauses: every
// clause of a group must hold (and), and any one group is enough (or).
//
// THREE ANSWERS, NOT TWO. A clause about the target has no answer while
// nobody is targeted; one about the attack has none with no weapon armed.
// `holds` then says `null` - unknown - and the caller adds nothing and
// shows the effect as waiting ("when..."), rather than guessing either
// way. A false clause still settles its group, and a true group still
// settles the whole, whatever else is unknown.
//
// A clause is a row in `CLAUSES`: how it is read from storage, how it is
// judged, how it is said. Adding a kind is a row there, or in
// `whenClauses` (the Armoury's: stance, toggles, weapon), which it composes.
//
// Pure. Zero React dependency.
// ============================================================
import { COND_NAMES, condType } from '../conditions';
import { SIZE_ORDER } from '../constants';
import { isHelpless } from '../helpless';
import { ORIGIN_CLASSES } from '../originClasses';
import { isUnaware } from '../unaware';
import { MORE_CLAUSES, SELF_KINDS, type MoreClause } from './whenClauses';
import { oneOf, type ClauseRule, type Raw, type Situation, type Somebody, type Verdict } from './whenParts';

export type { Situation } from './whenParts';

type Target = NonNullable<Situation['target']>;

/** The states a clause may ask about, each a test of somebody: their conditions, wounds, fatigue, frenzy. */
const STATES = {
  prone: { label: COND_NAMES.P, test: (t: Somebody) => has(t, 'P') },
  stunned: { label: COND_NAMES.S, test: (t: Somebody) => has(t, 'S') },
  blinded: { label: COND_NAMES.B, test: (t: Somebody) => has(t, 'B') },
  onFire: { label: COND_NAMES.Fi, test: (t: Somebody) => has(t, 'Fi') },
  snared: { label: COND_NAMES.Sn, test: (t: Somebody) => has(t, 'Sn') },
  unaware: { label: 'Unaware', test: (t: Somebody) => isUnaware(t, !!t.unaware) },
  helpless: {
    label: 'Helpless',
    test: (t: Somebody) => isHelpless({ conditions: [...(t.conditions ?? [])], horde: { isHorde: !!t.isHorde } }),
  },
  // The Armoury's (plan armoury, stage 3): facts of the sheet and the state, not conditions.
  wounded: { label: 'Wounded', test: (t: Somebody) => !!t.wounds && t.wounds.current < t.wounds.max },
  critical: { label: 'Critically wounded', test: (t: Somebody) => !!t.critical },
  fatigued: { label: 'Fatigued', test: (t: Somebody) => (t.fatigue ?? 0) > 0 },
  frenzied: { label: 'Frenzied', test: (t: Somebody) => !!t.frenzied },
  horde: { label: 'a Horde', test: (t: Somebody) => !!t.isHorde },
} as const;
type StateId = keyof typeof STATES;
/** What a stance may be up WHILE: the states that are conditions one carries. */
const OWN_STATES = ['prone', 'stunned', 'blinded', 'onFire', 'snared'] as const satisfies readonly StateId[];
export type OwnState = (typeof OWN_STATES)[number];
/** What a clause may ask of oneself: those, and one's wounds, fatigue and frenzy. */
const SELF_STATES = [
  ...OWN_STATES,
  'wounded',
  'critical',
  'fatigued',
  'frenzied',
] as const satisfies readonly StateId[];
type SelfState = (typeof SELF_STATES)[number];

const has = (t: Somebody, code: string): boolean => !!t.conditions?.some((c) => condType(c) === code);

interface Base {
  /** The clause holds when this is NOT so. */
  not?: true;
}
export type Clause =
  | (Base &
      (
        | { kind: 'attack'; is: 'melee' | 'ranged' }
        | { kind: 'target_size'; is: string; orLarger?: true }
        | { kind: 'target_origin'; is: string }
        | { kind: 'target_tag'; is: string }
        | { kind: 'target_state'; is: StateId }
        | { kind: 'own_state'; is: SelfState }
      ))
  | MoreClause;
export type ClauseKind = Clause['kind'];
/** Groups of clauses: all of a group, any of the groups. */
export type When = Clause[][];

type Of<K extends ClauseKind> = Extract<Clause, { kind: K }>;

const title = (word: string) => word.charAt(0) + word.slice(1).toLowerCase();
const MAX_TAG = 30;

/** A tag as it is compared: without case, and without a plural's ending - "Orks" is "ork", "witches" is "witch". */
const tagKey = (tag: string) =>
  tag
    .trim()
    .toLowerCase()
    .replace(/(ch|sh|x)es$/, '$1')
    .replace(/s$/, '');
const sameTag = (a: string, b: string) => tagKey(a) === tagKey(b);

/** A clause about the target: unknown while nobody is targeted. */
const ofTarget = (s: Situation, test: (t: Target) => boolean): Verdict => (s.target ? test(s.target) : null);
const sizeAt = (size: string | undefined) => SIZE_ORDER.indexOf(size || 'Average');
const stateOptions = (ids: readonly StateId[]) => ids.map((id) => ({ id, label: STATES[id].label }));
const ALL_STATES = Object.keys(STATES) as StateId[];

/** The own states on offer, with their names: what a stance may be up WHILE (`soloModes`). */
export const OWN_STATE_CHOICES = stateOptions(OWN_STATES) as { id: OwnState; label: string }[];

/** One of the own states off storage, or null. */
export const readOwnState = (raw: unknown): OwnState | null => oneOf(OWN_STATES, raw);

/** Does somebody carrying these conditions have this state? */
export function ownStateHolds(state: OwnState, conditions: readonly string[] | null | undefined): boolean {
  return STATES[state].test({ conditions: conditions ?? [] });
}

const CLAUSES: { [K in ClauseKind]: ClauseRule<Of<K>> } = {
  attack: {
    label: 'The attack is',
    blank: { kind: 'attack', is: 'melee' },
    options: [
      { id: 'melee', label: 'in melee' },
      { id: 'ranged', label: 'at range' },
    ],
    read: (raw) => {
      const is = oneOf(['melee', 'ranged'] as const, raw.is);
      return is ? { kind: 'attack', is } : null;
    },
    judge: (c, s) => (s.attack ? s.attack === c.is : null),
    say: (c) => (c.is === 'melee' ? ['in melee', 'not in melee'] : ['at range', 'not at range']),
  },
  target_size: {
    label: "The target's size is",
    blank: { kind: 'target_size', is: 'Hulking', orLarger: true },
    options: SIZE_ORDER.map((id) => ({ id, label: id })),
    read: (raw) => {
      const is = oneOf(SIZE_ORDER, raw.is);
      if (!is) return null;
      return raw.orLarger === true ? { kind: 'target_size', is, orLarger: true } : { kind: 'target_size', is };
    },
    judge: (c, s) =>
      ofTarget(s, (t) => (c.orLarger ? sizeAt(t.size) >= sizeAt(c.is) : sizeAt(t.size) === sizeAt(c.is))),
    say: (c) => {
      const size = c.orLarger ? `${c.is} or larger` : c.is;
      return [`the target is ${size}`, `the target is not ${size}`];
    },
  },
  target_origin: {
    label: 'The target is',
    blank: { kind: 'target_origin', is: 'XENOS' },
    options: ORIGIN_CLASSES.map((id) => ({ id, label: title(id) })),
    read: (raw) => {
      const is = oneOf(ORIGIN_CLASSES, raw.is);
      return is ? { kind: 'target_origin', is } : null;
    },
    judge: (c, s) => ofTarget(s, (t) => !!t.origins?.includes(c.is)),
    say: (c) => [`the target is ${title(c.is)}`, `the target is not ${title(c.is)}`],
  },
  target_tag: {
    label: 'The target is tagged',
    // Never empty: an empty tag is not a clause, and would vanish from the editor as it was added.
    blank: { kind: 'target_tag', is: 'tag' },
    read: (raw) => {
      const is = typeof raw.is === 'string' ? raw.is.trim().slice(0, MAX_TAG) : '';
      return is ? { kind: 'target_tag', is } : null;
    },
    judge: (c, s) => ofTarget(s, (t) => !!t.tags?.some((tag) => sameTag(tag, c.is))),
    say: (c) => [`the target is tagged "${c.is}"`, `the target is not tagged "${c.is}"`],
  },
  target_state: {
    label: 'The target is (state)',
    blank: { kind: 'target_state', is: 'prone' },
    options: stateOptions(ALL_STATES),
    read: (raw) => {
      const is = oneOf(ALL_STATES, raw.is);
      return is ? { kind: 'target_state', is } : null;
    },
    judge: (c, s) => ofTarget(s, STATES[c.is].test),
    say: (c) => [`the target is ${STATES[c.is].label}`, `the target is not ${STATES[c.is].label}`],
  },
  own_state: {
    label: 'I am',
    blank: { kind: 'own_state', is: 'prone' },
    options: stateOptions(SELF_STATES),
    read: (raw) => {
      const is = oneOf(SELF_STATES, raw.is);
      return is ? { kind: 'own_state', is } : null;
    },
    judge: (c, s) => (s.self ? STATES[c.is].test(s.self) : null),
    say: (c) => [`I am ${STATES[c.is].label}`, `I am not ${STATES[c.is].label}`],
  },
  ...MORE_CLAUSES,
};

const ruleOf = (kind: ClauseKind) => CLAUSES[kind] as unknown as ClauseRule<Clause>;

/** The kinds of clause on offer, in the editor's order, each with its values (absent: free text). */
export const CLAUSE_CHOICES = (Object.keys(CLAUSES) as ClauseKind[]).map((kind) => ({
  kind,
  label: CLAUSES[kind].label,
  options: CLAUSES[kind].options,
}));

/** A new clause of a kind, ready to be adjusted. */
export const blankClause = (kind: ClauseKind): Clause => ({ ...CLAUSES[kind].blank });

function readClause(raw: unknown): Clause | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Raw;
  const kind = oneOf(Object.keys(CLAUSES) as ClauseKind[], stored.kind);
  const clause = kind ? ruleOf(kind).read(stored) : null;
  if (!clause) return null;
  return stored.not === true ? { ...clause, not: true } : clause;
}

/** A stored condition, validated: clauses this build cannot judge are left out, and nothing left is no condition. */
export function readWhen(raw: unknown): When | undefined {
  if (!Array.isArray(raw)) return undefined;
  const groups = raw
    .map((group) => (Array.isArray(group) ? group.map(readClause).filter((c): c is Clause => c !== null) : []))
    .filter((group) => group.length > 0);
  return groups.length > 0 ? groups : undefined;
}

function judge(clause: Clause, s: Situation): Verdict {
  const verdict = ruleOf(clause.kind).judge(clause, s);
  return verdict === null || !clause.not ? verdict : !verdict;
}

/** All of them: one false settles it; else anything unknown leaves it unknown. */
function every(verdicts: Verdict[]): Verdict {
  if (verdicts.includes(false)) return false;
  return verdicts.includes(null) ? null : true;
}

/** Any of them: one true settles it; else anything unknown leaves it unknown. */
function some(verdicts: Verdict[]): Verdict {
  if (verdicts.includes(true)) return true;
  return verdicts.includes(null) ? null : false;
}

/** Does the condition hold in this situation? True, false, or null when the situation does not say. */
export function holds(when: When, situation: Situation): Verdict {
  return some(when.map((group) => every(group.map((clause) => judge(clause, situation)))));
}

/**
 * May a SHEET effect wait on this? Only when every clause is about oneself (the two tiers, plan armoury): a sheet
 * is worked out before anybody is targeted or anything armed, so a clause about those could never be answered.
 */
export function isSelfOnly(when: When): boolean {
  return when.every((group) => group.every((clause) => SELF_KINDS.includes(clause.kind)));
}

/** The condition in words: "in melee and the target is Hulking or larger, or the target is Xenos". */
export function whenSentence(when: When): string {
  const said = (clause: Clause) => ruleOf(clause.kind).say(clause)[clause.not ? 1 : 0];
  return when.map((group) => group.map(said).join(' and ')).join(', or ');
}
